/** @format */

import { Transition } from "@headlessui/react";
import React, { useCallback, useEffect } from "react";

import { useVersion } from "../api/versionAPI";
import { getSwRegistration } from "./serviceWorkerHelpers";

const UpdateToast = () => {
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const controllerAbortRef = React.useRef<AbortController | null>(null);
  const activationTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const {
    webUpdate,
    serverUpdate,
    setIgnored,
    resetWebUpdate,
    isStaff,
    localVersion,
    configVersion,
    liveServerVersion,
    deployId,
    hasWaitingSW,
    isWebUpdateAvailable,
  } = useVersion();

  // Cleanup listeners and timers on unmount
  useEffect(() => {
    return () => {
      controllerAbortRef.current?.abort();
      if (activationTimeoutRef.current) clearTimeout(activationTimeoutRef.current);
    };
  }, []);

  const activateWaitingSW = useCallback((reg: ServiceWorkerRegistration) => {
    // Abort any previous controllerchange listener to prevent duplicate reloads
    controllerAbortRef.current?.abort();
    if (activationTimeoutRef.current) clearTimeout(activationTimeoutRef.current);

    const controller = new AbortController();
    controllerAbortRef.current = controller;

    reg.waiting?.postMessage({ type: "SKIP_WAITING" });
    navigator.serviceWorker.addEventListener(
      "controllerchange",
      () => {
        if (activationTimeoutRef.current) clearTimeout(activationTimeoutRef.current);
        window.location.reload();
      },
      { signal: controller.signal }
    );

    // Fallback: if controllerchange doesn't fire within 5s, reload anyway
    activationTimeoutRef.current = setTimeout(() => {
      controller.abort();
      window.location.reload();
    }, 5_000);
  }, []);

  const update = useCallback(() => {
    const swRegistration = getSwRegistration();
    if (swRegistration?.waiting) {
      activateWaitingSW(swRegistration);
    } else {
      // Waiting SW gone (consumed by another tab?) — dismiss and let the
      // version poll trigger another SW update check on the next cycle.
      resetWebUpdate();
    }
  }, [activateWaitingSW, resetWebUpdate]);

  const ignore = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIgnored();
  };

  useEffect(() => {
    if (!webUpdate) return;

    const timeout = setTimeout(() => {
      update();
    }, 5500);

    return () => {
      clearTimeout(timeout);
    };
  }, [webUpdate, update]);

  return (
    <div className="fixed bottom-4 left-4 z-50 max-h-[calc(100vh-2rem)] overflow-y-auto duration-500 ease-in-out flex flex-col justify-end">
      <Transition
        as="div"
        show={serverUpdate}
        enter="duration-500 ease-in-out transition-transform"
        enterFrom="translate-y-36"
        enterTo="translate-y-0"
        leave="duration-500 ease-in-out transition-transform"
        leaveFrom="translate-y-0"
        leaveTo="translate-y-36"
      >
        <div className="w-80 bg-red-600 rounded-md px-3 py-2 mx-auto overflow-hidden shadow text-center animate-bounce">
          Update the Server!
        </div>
      </Transition>
      <Transition
        as="div"
        show={webUpdate}
        enter="duration-500 ease-in-out transition-transform"
        enterFrom="translate-y-36"
        enterTo="translate-y-0"
        leave="duration-500 ease-in-out transition-transform"
        leaveFrom="translate-y-0"
        leaveTo="translate-y-36"
      >
        <div className="w-80 bg-slate-900 rounded-md px-3 py-2 mx-auto overflow-clip shadow mt-2">
          <div className="flex items-center justify-between">
            <p className="text-lg font-medium">New Update!</p>
          </div>
          {isStaff && (
            <details className="mt-1">
              <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-400">
                Debug info
              </summary>
              <div className="text-xs text-slate-400 mt-1 font-mono space-y-0.5">
                <div>bundle: {localVersion}</div>
                <div>config: {String(configVersion)}</div>
                <div>live: {String(liveServerVersion)}</div>
                <div>deployId: {deployId}</div>
                <div>
                  waitingSW: {String(hasWaitingSW)} | swUpdate:{" "}
                  {String(isWebUpdateAvailable)}
                </div>
              </div>
            </details>
          )}
          <div className="flex items-center justify-between mt-2 w-full gap-x-2">
            <button
              type="button"
              onClick={ignore}
              className="text-slate-400 hover:text-slate-200 rounded bg-slate-700 px-2 py-1 text-sm"
            >
              Dismiss
            </button>
            <button
              type="button"
              onClick={update}
              className="w-full rounded animate-pulse px-2 py-1 text-sm relative"
            >
              Updating
              <div className="absolute top-0 left-0 h-full bg-green-600 rounded text-center px-2 fill-bar whitespace-nowrap text-slate-200 -z-10" />
            </button>
          </div>
        </div>
      </Transition>
    </div>
  );
};

export default UpdateToast;
